import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {SignUpViewModel} from "@app/presentation/pages/sign-up/sign-up.view-model";
import {ActualCostumerSignUpData} from "@app/core/user/domain/dtos/actual-costumer-sign-up-data";
import {NewCostumerSignUpData} from "@app/core/user/domain/dtos/new-costumer-sign-up-data";
import {PopupDispatcher} from "@app/presentation/components/errors/popup.dispatcher";
import {routesNames} from "@app/app-routing.module";
import {UserType} from '@app/core/user/domain/dtos/user-type.enum';
import {LocalizedRouter} from "@app/presentation/services/localized-router";
import {ExitService} from "@app/presentation/services/exit.service";
import {take, takeUntil} from "rxjs/operators";
import {DeliveryPoint} from "@app/core/delivery-point/application/dtos/delivery-point";
import {DeliveryPointService} from "@app/presentation/services/delivery-point/delivery-point.service";
import {ModalService} from "@app/presentation/services/modal/modal.service";
import {firstValueFrom, Subject} from "rxjs";
import {postalCodeValidator} from '@app/presentation/form-validators/postal-code-validator';
import {RecaptchaAction, RecaptchaService} from "@app/presentation/services/recaptcha/recaptcha.service";

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit, OnDestroy {

  private destroySubject: Subject<boolean> = new Subject<boolean>();
  private destroy$ = this.destroySubject.asObservable();

  isCompanyControl = new UntypedFormControl(null, Validators.required);

  isClientControl = new UntypedFormControl(null, Validators.required);

  clientCodeControl = new UntypedFormControl(null, Validators.required);

  dataForm = new UntypedFormGroup({
    name: new UntypedFormControl(null, Validators.required),
    surname: new UntypedFormControl(null, Validators.required),
    nif: new UntypedFormControl(null)
  });

  userPasswordForm = new UntypedFormGroup({
    email: new UntypedFormControl(null, [Validators.required, Validators.email]),
    password: new UntypedFormControl(null, [Validators.required, Validators.minLength(8)]),
    acceptTermsAndConditions: new UntypedFormControl(false, [Validators.required, Validators.requiredTrue])
  });

  postalCodeControl = new UntypedFormControl(null, [Validators.required,postalCodeValidator()]);

  isSignUpLoading$ = this.signUpViewModel.isSignUpLoading$;

  constructor(
    private signUpViewModel: SignUpViewModel,
    private popupDispatcher: PopupDispatcher,
    private localizedRouter: LocalizedRouter,
    private injector: Injector,
    private deliveryPointService: DeliveryPointService,
    private modalService: ModalService,
    private recaptchaService: RecaptchaService,
  ) {
    this.popupDispatcher.setNotifier(signUpViewModel.notifier);
    this.signUpViewModel.canSelectDeliveryPoint$.pipe(take(1)).subscribe({
      next: canSelectDeliveryPoint => {
        if (canSelectDeliveryPoint) {
          this.navigateToDeliveryPoints();
          return;
        }
        this.exit();
      }});
    this.listenForNoDeliveryPostalCodeConfirmationQuestion();
    this.listenIfIsSignedUp();
  }

  private listenForNoDeliveryPostalCodeConfirmationQuestion() {
    this.signUpViewModel.askForNoDeliveryPostalCodeConfirmation$
        .pipe(
            takeUntil(this.destroy$)
        )
        .subscribe({
          next: messatge => this.openNoDeliveryPostalCodeConfirmationModal(messatge)
        });
  }

  private listenIfIsSignedUp() {
    this.signUpViewModel.isSignedUp$.pipe(
        takeUntil(this.destroy$)
      ).subscribe({
          next: () => this.exit()
      });
  }

  ngOnInit(): void {
    this.isClientControl.valueChanges.subscribe({
      next: isClient => {
        if (isClient) {
          this.dataForm.controls.nif.setValidators(Validators.required);
        } else {
          this.dataForm.controls.nif.removeValidators(Validators.required);
        }
          this.dataForm.controls.nif.updateValueAndValidity();
      }
    });
    this.isCompanyControl.valueChanges.subscribe({
      next: isCompany => {
        if (!isCompany) {
          // If the user is not a company, set isClient to false because only companies can be clients.
          this.isClientControl.setValue(false)
        } else {
          // If the user is a company, set isClient to null because we don't know if the company is a client or not.
          this.isClientControl.setValue(null)
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroySubject.next(true);
    this.destroySubject.complete();
  }

  onboardingFinished(): void {
    this.signUp();
  }

  private async selectDeliveryPoint(deliveryPoints: DeliveryPoint[]): Promise<DeliveryPoint | null> {
    return this.deliveryPointService.selectDeliveryPoint(deliveryPoints);
  }

  private signUp(noDelivery: boolean = false) {
    if (this.isClientControl.value && this.isCompanyControl.value) {
      this.signUpActualCostumer(noDelivery);
    } else {
      this.signUpNewCostumer(noDelivery);
    }
  }

  private async signUpActualCostumer(noDelivery: boolean) {
    const recaptchaToken =  await firstValueFrom(this.recaptchaService.getToken(RecaptchaAction.SIGN_IN));
    const actualCustomerSignData: ActualCostumerSignUpData = {
      name: this.dataForm.controls.name.value,
      surname: this.dataForm.controls.surname.value,
      nif: this.dataForm.controls.nif.value,
      email: this.userPasswordForm.controls.email.value,
      password: this.userPasswordForm.controls.password.value,
      customerCode: this.clientCodeControl.value,
      userType: UserType.ENTERPRISE,
      noDelivery: noDelivery,
      recaptchaToken: recaptchaToken,
    };
    this.signUpViewModel.signUpActualCostumer(actualCustomerSignData, this.selectDeliveryPoint).then(
        () => this.exit()
    );
  }

  private async signUpNewCostumer(noDelivery: boolean) {
    const recaptchaToken =  await firstValueFrom(this.recaptchaService.getToken(RecaptchaAction.SIGN_IN));
    const newCostumerSignUpData: NewCostumerSignUpData = {
      name: this.dataForm.controls.name.value,
      surname: this.dataForm.controls.surname.value,
      email: this.userPasswordForm.controls.email.value,
      password: this.userPasswordForm.controls.password.value,
      postalCode: this.postalCodeControl.value,
      userType: this.isCompanyControl.value ? UserType.ENTERPRISE : UserType.INDIVIDUAL,
      noDelivery: noDelivery,
      recaptchaToken: recaptchaToken,
    };

    this.signUpViewModel.signUpNewCostumer(newCostumerSignUpData, this.selectDeliveryPoint).then(
        () => this.exit()
    );
  }

  returnToThePreviousPage() {
    ExitService.back(this.injector);
  }

  private exit(): void {
    if (ExitService.hasReturnUrl(this.injector)) {
      ExitService.navigateToReturnUrl(this.injector);
      return;
    }
    this.localizedRouter.navigate(['/' + routesNames.home]);
  }

  private navigateToDeliveryPoints() {
      this.localizedRouter.navigate(['/', routesNames.deliveryPoint])
  }

  openNoDeliveryPostalCodeConfirmationModal(message: string) {
    this.modalService.display(message,isResponsePositive => {
      if (isResponsePositive) {
        this.signUp(true);
      }
    });
  }
}
